import { useState, useEffect } from 'react';
import { dataApi } from '@/lib/api';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, AreaChart, Area } from 'recharts';
import { MapContainer, TileLayer, CircleMarker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

const COLORS = ['#F66922', '#10B981', '#3B82F6', '#8B5CF6', '#EC4899'];
const chartTooltipStyle = { background: '#fff', border: '1px solid #E2E8F0', borderRadius: 8, boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)', fontSize: 12 };

function formatNum(n) {
  if (!n) return '0';
  if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
  if (n >= 1000) return (n / 1000).toFixed(1) + 'k';
  return n.toLocaleString();
}

export default function Analytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeframe, setTimeframe] = useState(90);

  useEffect(() => {
    setLoading(true);
    dataApi.getAnalytics(timeframe)
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [timeframe]);

  if (loading) return <div className="space-y-6">{[1,2,3].map(i => <div key={i} className="h-64 skeleton-shimmer rounded-lg" />)}</div>;
  if (!data) return null;

  const { trends, funnel, geography, industries, monthly_topics } = data;

  // Build stacked bar data from monthly_topics
  const months = Object.keys(monthly_topics || {}).sort();
  const allTopicNames = new Set();
  months.forEach(m => (monthly_topics[m] || []).forEach(t => allTopicNames.add(t.name)));
  const topTopics = [...allTopicNames].slice(0, 5);
  const stackedData = months.map(m => {
    const entry = { month: m };
    topTopics.forEach(tn => {
      const found = (monthly_topics[m] || []).find(t => t.name === tn);
      entry[tn] = found ? found.count : 0;
    });
    return entry;
  });

  // Funnel data
  const funnelStages = [
    { stage: 'Prospects', value: funnel?.prospects || 0 },
    { stage: 'MQL', value: funnel?.mql || 0 },
    { stage: 'SQL', value: funnel?.sql_count || 0 },
    { stage: 'Won', value: funnel?.won || 0 },
  ];

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-[#0A0A0A] tracking-tight">Analytics</h1>
        <div className="flex bg-white border border-neutral-200 rounded-lg overflow-hidden">
          {[{ l: '30 Days', v: 30 }, { l: '60 Days', v: 60 }, { l: '90 Days', v: 90 }, { l: '180 Days', v: 180 }].map(t => (
            <button key={t.v} data-testid={`analytics-tf-${t.v}`} onClick={() => setTimeframe(t.v)}
              className={`px-3 py-1.5 text-xs font-medium transition-all ${timeframe === t.v ? 'bg-[#F66922] text-white' : 'text-neutral-500 hover:bg-neutral-50'}`}>
              {t.l}
            </button>
          ))}
        </div>
      </div>

      {/* Engagement Trends */}
      <div className="oaa-card p-5" data-testid="engagement-trends">
        <h3 className="text-base font-semibold text-[#0A0A0A] mb-4">Engagement Trends</h3>
        <ResponsiveContainer width="100%" height={280}>
          <AreaChart data={trends}>
            <defs>
              <linearGradient id="engGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#F66922" stopOpacity={0.15} />
                <stop offset="95%" stopColor="#F66922" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
            <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#94A3B8' }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
            <YAxis tick={{ fontSize: 10, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={chartTooltipStyle} />
            <Area type="monotone" dataKey="count" stroke="#F66922" strokeWidth={2} fill="url(#engGrad)" name="Engagements" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Topics by Month */}
        <div className="oaa-card p-5" data-testid="topics-by-month">
          <h3 className="text-base font-semibold text-[#0A0A0A] mb-4">Top Topics by Month</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={stackedData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={chartTooltipStyle} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              {topTopics.map((tn, i) => (
                <Bar key={tn} dataKey={tn} stackId="a" fill={COLORS[i % COLORS.length]} radius={i === topTopics.length - 1 ? [3, 3, 0, 0] : [0, 0, 0, 0]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Funnel */}
        <div className="oaa-card p-5" data-testid="funnel-chart">
          <h3 className="text-base font-semibold text-[#0A0A0A] mb-4">Conversion Funnel</h3>
          <div className="space-y-3 py-4">
            {funnelStages.map((stage, i) => {
              const maxVal = funnelStages[0].value || 1;
              const pct = (stage.value / maxVal) * 100;
              const conversionRate = i > 0 ? ((stage.value / (funnelStages[i - 1].value || 1)) * 100).toFixed(1) : null;
              return (
                <div key={stage.stage}>
                  {conversionRate && (
                    <div className="flex justify-center mb-1">
                      <span className="text-[10px] font-medium text-neutral-300">{conversionRate}% conversion</span>
                    </div>
                  )}
                  <div className="flex items-center gap-3">
                    <span className="w-20 text-xs font-medium text-neutral-600 text-right">{stage.stage}</span>
                    <div className="flex-1 h-8 bg-neutral-100 rounded relative overflow-hidden" style={{ maxWidth: `${pct}%`, minWidth: '60px' }}>
                      <div className="h-full rounded flex items-center justify-center" style={{ background: COLORS[i], width: '100%' }}>
                        <span className="text-white text-xs font-bold">{formatNum(stage.value)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Geography Heatmap */}
        <div className="oaa-card p-5" data-testid="geography-heatmap">
          <h3 className="text-base font-semibold text-[#0A0A0A] mb-4">Geography Heatmap</h3>
          <div className="h-[300px] rounded-lg overflow-hidden">
            <MapContainer center={[20, 0]} zoom={2} style={{ height: '100%', width: '100%' }} scrollWheelZoom={false}>
              <TileLayer attribution="&copy; OpenStreetMap, &copy; CartoDB" url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png" />
              {geography.map((g, i) => {
                const maxCount = geography[0]?.count || 1;
                const opacity = 0.3 + (g.count / maxCount) * 0.5;
                return (
                  <CircleMarker key={i} center={[g.lat, g.lng]} radius={Math.max(6, Math.min(g.count / 200, 30))}
                    pathOptions={{ fillColor: '#F66922', fillOpacity: opacity, color: '#F66922', weight: 1 }}>
                    <Popup><span className="text-xs font-medium">{g.country}: {formatNum(g.count)} engagements</span></Popup>
                  </CircleMarker>
                );
              })}
            </MapContainer>
          </div>
        </div>

        {/* Industry Breakdown */}
        <div className="oaa-card p-5" data-testid="industry-breakdown">
          <h3 className="text-base font-semibold text-[#0A0A0A] mb-4">Top Industries</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={industries} layout="vertical" margin={{ left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 10, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="industry" tick={{ fontSize: 10, fill: '#64748B' }} axisLine={false} tickLine={false} width={150} />
              <Tooltip contentStyle={chartTooltipStyle} />
              <Bar dataKey="count" fill="#F66922" radius={[0, 4, 4, 0]} barSize={16} name="Engagements" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
